import { Button } from "@svea/design-system-components-react";
import { ButtonGroup, P, Stack, useText } from "@sveabank/cc-common";
import { useState } from "react";
import { usePageContext } from "../../../../hooks/context";
import type { BeneficialOwnershipStatus } from "../../../../types/beneficial-ownership";
import { stepIs } from "../../../../utils/application";
import { useKyc } from "../../hooks";
import BeneficialOwnerExists from "./beneficial-owner-exists";
import BeneficialOwnerMissing from "./beneficial-owner-missing";
import BoInfoModal from "./bo-info-modal";
import NoBeneficialOwner from "./no-beneficial-owner";

type Props = {
	onSubmit: (arg: boolean) => Promise<void>;
};

const BeneficialOwnership = ({ onSubmit }: Props) => {
	const {
		beneficialOwnership: {
			beneficialOwners,
			beneficialOwnershipStatus: savedStatus,
			foreignBeneficialOwners,
			beneficialOwnerConfirmationText,
		},
		isSubmittingBeneficialOwner,
		steps,
	} = usePageContext(useKyc);
	const [t, texts] = useText();
	const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

	const originalStatus =
		steps?.find(stepIs("ConfirmBeneficialOwnershipStep"))?.state.beneficialOwnershipStatus ||
		"unspecifiedOrMissingInformation";

	const [currentStatus, setCurrentStatus] = useState<BeneficialOwnershipStatus>(savedStatus);

	const canUndo =
		originalStatus !== "unspecifiedOrMissingInformation" && originalStatus !== currentStatus;

	return (
		<Stack gap={4} fullSize align="start">
			<Button
				variant="plain"
				iconStart="informationCircle"
				onPress={() => {
					setIsInfoModalOpen(true);
				}}
			>
				{t(texts.kyc.beneficialOwnership.infoHeading)}
			</Button>
			<Stack justify="end" direction="row" fullSize>
				{originalStatus !== "unspecifiedOrMissingInformation" && (
					<Button
						onPress={() => {
							setCurrentStatus(
								currentStatus === "unspecifiedOrMissingInformation"
									? originalStatus
									: "unspecifiedOrMissingInformation",
							);
						}}
						variant="plain"
					>
						{t(
							canUndo
								? texts.kyc.beneficialOwnership.button.undo
								: texts.kyc.beneficialOwnership.button.invalid,
						)}
					</Button>
				)}
			</Stack>
			{currentStatus === "beneficialOwnersExists" && (
				<BeneficialOwnerExists
					beneficialOwners={beneficialOwners}
					foreignBeneficialOwners={foreignBeneficialOwners}
				/>
			)}
			{currentStatus === "noBeneficialOwnersExist" && <NoBeneficialOwner />}
			{currentStatus === "unspecifiedOrMissingInformation" && <BeneficialOwnerMissing />}
			<ButtonGroup row spaceBetween>
				<Button
					data-testid="confirmBeneficialOwnersButton"
					isPending={isSubmittingBeneficialOwner}
					onPress={() => onSubmit(currentStatus !== "unspecifiedOrMissingInformation")}
				>
					{t(texts.common.confirm)}
				</Button>
				{currentStatus !== "unspecifiedOrMissingInformation" && (
					<P>{beneficialOwnerConfirmationText}</P>
				)}
			</ButtonGroup>
			<BoInfoModal isOpen={isInfoModalOpen} setOpen={setIsInfoModalOpen} />
		</Stack>
	);
};

export default BeneficialOwnership;
